import {
  buildContentFamiliaritySummary,
  type ContentFamiliaritySummary,
  type GrammarFamiliarityStatus,
  type WordFamiliarityStatus
} from './familiarity-core'
import type { StoryLearningIndexItem, StoryMetadata } from '@/content/stories'
import type { LearningStatus } from './learning-state-core'
import { isStoryRead, isStoryStarted } from './content-progress-core'

export const STORY_RECOMMENDATION_CANDIDATE_LIMIT = 12
export const STORY_RECOMMENDATION_LIMIT = 5

export type StoryProgressLike = {
  status?: LearningStatus | null
  progress?: number | null
}

export type StoryRecommendation = {
  story: StoryLearningIndexItem
  score: number
  reasons: string[]
  familiarity: ContentFamiliaritySummary
  progress: StoryProgressLike | null
}

function dateScore(value?: string) {
  const timestamp = new Date(value || '').getTime()
  if (!Number.isFinite(timestamp)) return 0
  return Math.max(0, Math.min(1, timestamp / Date.now()))
}

function topicOverlap(a: string[] = [], b: string[] = []) {
  if (!a.length || !b.length) return 0
  const bSet = new Set(b)
  return (
    a.filter((topic) => bSet.has(topic)).length / Math.max(a.length, b.length)
  )
}

function isCompleted(progress: StoryProgressLike | null | undefined) {
  return isStoryRead(progress)
}

function isStarted(progress: StoryProgressLike | null | undefined) {
  return isStoryStarted(progress)
}

export function getNextChapterSlug(
  currentStory: Pick<StoryMetadata, 'seriesId' | 'chapterNumber'> | null,
  stories: Array<Pick<StoryMetadata, 'slug' | 'seriesId' | 'chapterNumber'>>
) {
  if (!currentStory?.seriesId || !currentStory.chapterNumber) return null
  return (
    stories.find(
      (story) =>
        story.seriesId === currentStory.seriesId &&
        story.chapterNumber === currentStory.chapterNumber! + 1
    )?.slug ?? null
  )
}

export function selectStoryRecommendationCandidates({
  stories,
  progressBySlug,
  currentSlug,
  limit = STORY_RECOMMENDATION_CANDIDATE_LIMIT
}: {
  stories: StoryLearningIndexItem[]
  progressBySlug?: Map<string, StoryProgressLike>
  currentSlug?: string | null
  limit?: number
}) {
  const currentStory = currentSlug
    ? (stories.find((story) => story.slug === currentSlug) ?? null)
    : null
  const nextChapterSlug = getNextChapterSlug(currentStory, stories)
  const currentTopics = currentStory?.topics ?? []

  return stories
    .filter((story) => story.slug !== currentSlug)
    .map((story) => {
      const progress = progressBySlug?.get(story.slug)
      const continuationScore =
        story.slug === nextChapterSlug
          ? 100
          : story.seriesId && story.seriesId === currentStory?.seriesId
            ? 30
            : 0
      const progressScore = isStarted(progress)
        ? 55
        : isCompleted(progress)
          ? -80
          : 18
      const topicScore = topicOverlap(currentTopics, story.topics) * 18
      const recencyScore =
        dateScore(story.publishedAt || story.updatedAt || story.createdAt) * 8

      return {
        story,
        score: continuationScore + progressScore + topicScore + recencyScore
      }
    })
    .sort(
      (a, b) => b.score - a.score || a.story.slug.localeCompare(b.story.slug)
    )
    .slice(0, Math.max(0, limit))
    .map((item) => item.story)
}

function recommendationReasons({
  story,
  familiarity,
  progress,
  currentStory,
  nextChapterSlug
}: {
  story: StoryLearningIndexItem
  familiarity: ContentFamiliaritySummary
  progress?: StoryProgressLike
  currentStory: StoryLearningIndexItem | null
  nextChapterSlug: string | null
}) {
  const reasons: string[] = []
  if (story.slug === nextChapterSlug) reasons.push('Next chapter')
  if (isStarted(progress)) reasons.push('Continue reading')
  if (familiarity.totalWordCount > 0) {
    reasons.push(`${Math.round(familiarity.seenWordRatio * 100)}% words seen`)
  }
  if (currentStory && topicOverlap(currentStory.topics, story.topics) > 0) {
    reasons.push('Same topic')
  }
  if (!progress?.status && !progress?.progress) reasons.push('New story')
  return reasons.slice(0, 3)
}

export function scoreStoryRecommendations({
  candidates,
  allStories,
  progressBySlug,
  wordStatuses,
  grammarStatuses,
  currentSlug,
  limit = STORY_RECOMMENDATION_LIMIT
}: {
  candidates: StoryLearningIndexItem[]
  allStories: StoryLearningIndexItem[]
  progressBySlug?: Map<string, StoryProgressLike>
  wordStatuses?: Map<number, WordFamiliarityStatus>
  grammarStatuses?: Map<string, GrammarFamiliarityStatus>
  currentSlug?: string | null
  limit?: number
}): StoryRecommendation[] {
  const currentStory = currentSlug
    ? (allStories.find((story) => story.slug === currentSlug) ?? null)
    : null
  const nextChapterSlug = getNextChapterSlug(currentStory, allStories)

  return candidates
    .map((story) => {
      const progress = progressBySlug?.get(story.slug)
      const familiarity = buildContentFamiliaritySummary({
        contentType: 'story',
        contentId: story.slug,
        wordIds: story.wordIds,
        wordStatuses,
        grammarConceptIds: story.grammarConceptIds,
        grammarStatuses,
        progressRatio: progress?.progress ?? null
      })
      const continuationScore = story.slug === nextChapterSlug ? 1.2 : 0
      const progressScore = isStarted(progress)
        ? 0.8
        : isCompleted(progress)
          ? -2
          : 0.25
      const topicScore = currentStory
        ? topicOverlap(currentStory.topics, story.topics) * 0.25
        : 0
      const score =
        familiarity.familiarityScore * 1.2 +
        continuationScore +
        progressScore +
        topicScore

      return {
        story,
        score,
        familiarity,
        progress: progress ?? null,
        reasons: recommendationReasons({
          story,
          familiarity,
          progress,
          currentStory,
          nextChapterSlug
        })
      }
    })
    .filter(
      (recommendation) =>
        !isCompleted(progressBySlug?.get(recommendation.story.slug))
    )
    .sort(
      (a, b) => b.score - a.score || a.story.slug.localeCompare(b.story.slug)
    )
    .slice(0, Math.max(0, limit))
}
